import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { EyeIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

const MySwal = withReactContent(Swal);

// Modal Component for Viewing Subscription Details
const SubscriptionViewModal = ({ isOpen, onClose, subscription }) => {
    if (!isOpen || !subscription) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[300]">
            <div className="bg-[#0B2C3D] p-8 rounded-2xl shadow-2xl w-full max-w-lg transform transition-all duration-300">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-extrabold text-white">Subscription Details</h3>
                    <button onClick={onClose} className="text-white hover:text-[#B39359] transition-colors duration-200">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-white">Email</label>
                        <p className="mt-1 text-white">{subscription.email || "N/A"}</p>
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="py-2 px-5 rounded-lg bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-300"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function Subscriptions() {
    const [subscriptions, setSubscriptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const token = localStorage.getItem("token");

    // Fetch all subscriptions
    useEffect(() => {
        const fetchSubscriptions = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get("https://ligasos-laravel.ligasos.co/api/subscriptions", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setSubscriptions(response.data);
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Failed to fetch subscriptions. Please try again.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
                setIsLoading(false);
            }
        };
        fetchSubscriptions();
    }, [token]);

    // Handle Delete Subscription
    const handleDeleteSubscription = async (id) => {
        const result = await MySwal.fire({
            title: "Are you sure?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#B39359",
            cancelButtonColor: "#dc2626",
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                await axios.delete(`https://ligasos-laravel.ligasos.co/api/subscriptions/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setSubscriptions(subscriptions.filter((s) => s.id !== id));
                MySwal.fire({
                    icon: "success",
                    title: "Deleted",
                    text: "Subscription deleted successfully!",
                    confirmButtonColor: "#B39359",
                    timer: 1500,
                    showConfirmButton: false,
                });
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: err.response?.data?.message || "Failed to delete subscription.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Handle View Subscription
    const handleViewSubscription = (subscription) => {
        setSelectedSubscription(subscription);
        setIsViewModalOpen(true);
    };

    return (
        <section className="p-8 min-h-screen">
            <div className="">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-4xl font-extrabold text-[#0B2C3D] tracking-tight">Subscriptions</h2>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B39359]"></div>
                    </div>
                ) : subscriptions.length === 0 ? (
                    <p className="text-center text-gray-500 text-lg font-medium">No subscriptions found.</p>
                ) : (
                    <div className="overflow-x-auto bg-white rounded-xl shadow-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-[#0B2C3D] sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Email</th>
                                    <th className="px-6 py-4 text-right text-sm font-semibold text-white uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {subscriptions.map((subscription, index) => (
                                    <tr
                                        key={subscription.id}
                                        className={`hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                                    >
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{subscription.email || "N/A"}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => handleViewSubscription(subscription)}
                                                    className="p-2 text-[#0B2C3D] hover:bg-[#0B2C3D] hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <EyeIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteSubscription(subscription.id)}
                                                    className="p-2 text-red-600 hover:bg-red-600 hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            <SubscriptionViewModal
                isOpen={isViewModalOpen}
                onClose={() => {
                    setIsViewModalOpen(false);
                    setSelectedSubscription(null);
                }}
                subscription={selectedSubscription}
            />
        </section>
    );
}